import React, { useState } from 'react';
import { Palette, Component, Table2, Type } from 'lucide-react';
import PageShell from '../components/PageShell';
import Button from '@shared/ui/Button';
import Chip from '../components/Chip';
import FilterActions from '../components/FilterActions';
import ExportMenu from '../components/ExportMenu';
import ViewToggle from '../components/ViewToggle';
import RefreshButton from '../components/RefreshButton';
import SegmentedControl from '../components/SegmentedControl';
import Pagination from '../components/Pagination';
import EmptyState from '../components/EmptyState';
import StatCard, { StatCardRow } from '../components/StatCard';
import SectionHeading from '../components/SectionHeading';
import {
  StandardTable,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableBody,
  StandardTableRow,
  StandardTableCell,
} from '../components/StandardTable';
import {
  GroupedTableContainer,
  GroupedTableToolbar,
  GroupedTableHeader,
  GroupedTableHeaderCell,
  GroupedTableGroupRow,
  GroupedTableMetricCell,
  GroupedTableRow,
  ChildRowMarker,
  GroupedTableNotice,
} from '../components/GroupedTable';
import * as Icons from '../components/icons';
import ActionsHeaderHelp from '../components/ActionsHeaderHelp';
import { formatDate, formatDateTime, formatDateShort, formatTime, EMPTY_VALUE } from '../utils/dateUtils';
import ReportProblemDialog from '../components/report/ReportProblemDialog';
import CommitteeLink from '../components/committee/CommitteeLink';
import type { CommitteeBrief } from '../components/committee/committeeTypes';

/**
 * /design-system — the living style guide (internal).
 *
 * Every shared component of the pro web design system, rendered live with the
 * real theme, so engineers see the right way before hand-rolling a wrong one.
 * Rules and rationale: Documentation/FRONTEND_DESIGN_CONVENTIONS.md.
 * This page is also the intended first target for visual-regression
 * screenshots (issue #1129).
 */

const Section: React.FC<{ icon: React.ElementType; title: string; children: React.ReactNode }> = ({
  icon: Icon,
  title,
  children,
}) => (
  <section className="card space-y-4">
    <h2 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary flex items-center gap-2">
      <Icon className="w-5 h-5 text-primary-700 dark:text-primary-400" aria-hidden="true" />
      {title}
    </h2>
    {children}
  </section>
);

const Swatch: React.FC<{ name: string; className: string }> = ({ name, className }) => (
  <div className="flex flex-col items-center gap-1">
    <div className={`w-14 h-10 rounded-md border border-border-light dark:border-border-dark ${className}`} />
    <span className="text-[11px] text-text-tertiary dark:text-text-dark-tertiary">{name}</span>
  </div>
);

const DEMO_ROWS = [
  { id: 1, name: 'First row', status: 'Active', date: '2026-05-05' },
  { id: 2, name: 'Second row', status: 'Pending', date: '2026-06-01' },
  { id: 3, name: 'Third row', status: 'Closed', date: null },
];

const SEMANTIC_ICONS = Object.entries(Icons) as Array<[string, React.ElementType]>;

/** Report-a-problem demo: the real dialog with a stubbed send, so nothing reaches the network. */
const ReportProblemDemo: React.FC = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
        In product, <code>components/report/ReportProblemLink</code> renders the inline &ldquo;Report a problem&rdquo; link that
        opens this dialog. This demo sends nothing; include &ldquo;fail&rdquo; in the details to see the retry error.
      </p>
      <Button variant="secondary" onClick={() => setOpen(true)}>Open the report dialog</Button>
      <ReportProblemDialog
        isOpen={open}
        onClose={() => setOpen(false)}
        entityType="person"
        entityId="demo"
        section="campaign_money"
        pageTitle="Design system demo"
        submitReport={(payload) => new Promise((resolve, reject) => {
          setTimeout(() => (/fail/i.test(payload.details) ? reject({ response: { status: 502 } }) : resolve()), 600);
        })}
      />
    </>
  );
};

/** Committee card demo fixtures — passed as `brief`, so nothing reaches the network. */
const DEMO_ISSUE_COMMITTEE: CommitteeBrief = {
  co_id: 'demo-issue',
  name: 'Coloradans for Safe Streets',
  type: 'Issue Committee',
  status: 'active',
  closed_on: null,
  registered_on: '2025-03-04',
  purpose: 'To support Amendment 81 on the 2026 ballot.',
  candidate: null,
  cycle: '2025–2026',
  as_of: '2026-09-28',
  totals: { raised: 1250000, spent: 410000, cash_on_hand: 840000 },
  measures: [
    { id: 'demo-81', number: 'Amendment 81', title: 'Safe Streets and Sidewalks', side: 'support', election_year: 2026 },
    { id: 'demo-kk', number: 'Proposition KK', title: 'Parks Funding Measure', side: 'oppose', election_year: 2026 },
  ],
  registered_agent: 'Pat Example',
  top_donors: [
    { name: 'Example Builders Association', city: 'Denver', amount: 250000 },
    { name: 'Sample Fund', city: 'Boulder', amount: 100000 },
  ],
  tracer_url: 'https://tracer.sos.colorado.gov/PublicSite/SearchPages/CommitteeSearch.aspx',
};
const DEMO_CANDIDATE_COMMITTEE: CommitteeBrief = {
  ...DEMO_ISSUE_COMMITTEE,
  co_id: 'demo-candidate',
  name: 'Friends of Alex Sample',
  type: 'Candidate Committee',
  status: 'dormant',
  purpose: null,
  candidate: { person_id: 'demo-person', name: 'Alex Sample', office: 'State Senate, District 4' },
  measures: [],
};

const DesignSystemPage: React.FC = () => {
  const [view, setView] = useState<'card' | 'table'>('table');
  const [segment, setSegment] = useState<'one' | 'two' | 'three'>('one');
  const [page, setPage] = useState(2);
  const [demoFilter, setDemoFilter] = useState(true);
  const [demoGroupBy, setDemoGroupBy] = useState<'none' | 'client' | 'bill'>('client');
  const [demoOpenGroup, setDemoOpenGroup] = useState<string | null>('acme');

  const DEMO_GROUP_GRID = 'grid-cols-[7rem_minmax(0,1fr)_5rem]';

  return (
    <PageShell
      title="Design system"
      description="The shared components, rendered live — see Documentation/FRONTEND_DESIGN_CONVENTIONS.md for the rules"
    >
      <div className="space-y-6">
        <Section icon={Palette} title="Color roles">
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            All colors come from tokens — tailwind.config.js is the only place a hex may live.
          </p>
          <div className="flex flex-wrap gap-3">
            <Swatch name="primary-700" className="bg-primary-700" />
            <Swatch name="primary-400" className="bg-primary-400" />
            <Swatch name="primary-tint" className="bg-primary-tint" />
            <Swatch name="table header" className="bg-[color:var(--table-header-bg)]" />
            <Swatch name="report header" className="bg-[color:var(--report-header-bg)]" />
            <Swatch name="success" className="bg-success-600" />
            <Swatch name="warning" className="bg-warning-500" />
            <Swatch name="error" className="bg-error-600" />
            <Swatch name="neutral-200" className="bg-neutral-200" />
          </div>
        </Section>

        <Section icon={Component} title="Buttons — one primary per group, emphasis left to right">
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="ghost" size="sm">Ghost</Button>
            <Button variant="outline" size="sm">Outline</Button>
            <Button variant="secondary" size="sm">Secondary</Button>
            <Button variant="danger" size="sm">Danger</Button>
            <Button variant="primary" size="sm">Primary</Button>
            <Button variant="primary" size="sm" loading>Loading</Button>
            <Button variant="secondary" size="sm" disabled>Disabled</Button>
          </div>
        </Section>

        <Section icon={Component} title="Filter actions — Clear (disabled, never hidden) / Save / Search">
          <FilterActions
            onClear={() => setDemoFilter(false)}
            onSave={() => undefined}
            onSearch={() => setDemoFilter(true)}
            hasFilters={demoFilter}
          />
        </Section>

        <Section icon={Component} title="Toolbar controls">
          <div className="flex flex-wrap items-center gap-4">
            <ViewToggle value={view} onChange={setView} />
            <SegmentedControl
              ariaLabel="Demo segments"
              value={segment}
              onChange={setSegment}
              options={[
                { id: 'one', label: 'One' },
                { id: 'two', label: 'Two' },
                { id: 'three', label: 'Three' },
              ]}
            />
            <RefreshButton onRefresh={() => new Promise((r) => setTimeout(r, 800))} />
            <RefreshButton iconOnly onRefresh={() => new Promise((r) => setTimeout(r, 800))} />
            <ExportMenu
              formats={['csv', 'excel', { format: 'pdf', note: 'Landscape' }]}
              onExport={() => new Promise((r) => setTimeout(r, 600))}
            />
          </div>
        </Section>

        <Section icon={Component} title="Metrics — StatCard tiles in a StatCardRow">
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            Every &quot;big number + label&quot; summary is a <code>StatCard</code> inside a{' '}
            <code>StatCardRow</code> (components/StatCard.tsx) — never a hand-rolled bold div.
            Variants carry meaning; pass <code>onClick</code> for a drill-down tile.
            Reference adopter: the legislator Voting History summary.
          </p>
          <StatCardRow>
            <StatCard value="63%" label="Yes (608)" variant="success" />
            <StatCard value="32%" label="No (302)" variant="danger" />
            <StatCard value="5%" label="Excused (48)" variant="warning" />
            <StatCard value="0%" label="Absent (1)" />
            <StatCard value={959} label="Total Votes" variant="info" />
            <StatCard value={12} label="Drill-down" variant="info" onClick={() => undefined} />
          </StatCardRow>
          <StatCardRow>
            <StatCard value={42} label="With icon" variant="info" icon={Icons.AnalyticsIcon} />
            <StatCard value="1.2s" label="With sublabel" sublabel="per job" />
            <StatCard value={0} label="Loading" loading />
          </StatCardRow>
        </Section>

        <Section icon={Component} title="Section headings — SectionHeading">
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            In-page section titles render through <code>SectionHeading</code>{' '}
            (components/SectionHeading.tsx): bold uppercase label in the strong header purple —
            never the light <code>primary-400</code> tint — with an optional muted note, a hairline,
            and a right-aligned slot. Muted text is the <code>text-text-*</code> ink scale, never
            Tailwind <code>blue-*</code>. Reference adopter: the Reports page shelves.
          </p>
          <SectionHeading note="8 maintained by Capitol Commons" right="Right-aligned hint text">
            Standard reports
          </SectionHeading>
          <SectionHeading note="3 built by your organization">Your reports</SectionHeading>
        </Section>

        <Section icon={Table2} title="Tables — purple data / grey report">
          <StandardTable>
            <StandardTableHeader>
              <StandardTableRow>
                <StandardTableHeaderCell sortable sortKey="name" currentSortKey="name" sortDirection="asc" onSort={() => undefined}>
                  Data table
                </StandardTableHeaderCell>
                <StandardTableHeaderCell>Status</StandardTableHeaderCell>
                <StandardTableHeaderCell>Date</StandardTableHeaderCell>
              </StandardTableRow>
            </StandardTableHeader>
            <StandardTableBody>
              {DEMO_ROWS.map((r) => (
                <StandardTableRow key={r.id}>
                  <StandardTableCell>{r.name}</StandardTableCell>
                  <StandardTableCell><Chip label={r.status} size="sm" /></StandardTableCell>
                  <StandardTableCell>{r.date ? formatDate(r.date) : EMPTY_VALUE}</StandardTableCell>
                </StandardTableRow>
              ))}
            </StandardTableBody>
          </StandardTable>

          <StandardTable variant="report">
            <StandardTableHeader>
              <StandardTableRow>
                <StandardTableHeaderCell>Report table</StandardTableHeaderCell>
                <StandardTableHeaderCell>Status</StandardTableHeaderCell>
                <StandardTableHeaderCell>Timestamp</StandardTableHeaderCell>
              </StandardTableRow>
            </StandardTableHeader>
            <StandardTableBody>
              {DEMO_ROWS.map((r) => (
                <StandardTableRow key={r.id}>
                  <StandardTableCell>{r.name}</StandardTableCell>
                  <StandardTableCell>{r.status}</StandardTableCell>
                  <StandardTableCell>
                    {r.date ? formatDateTime(`${r.date}T15:42:00Z`) : EMPTY_VALUE}
                  </StandardTableCell>
                </StandardTableRow>
              ))}
            </StandardTableBody>
          </StandardTable>

          <Pagination page={page} pageCount={5} total={92} pageSize={20} noun="rows" onPageChange={setPage} />
        </Section>

        <Section icon={Table2} title="Actions legend — the ? beside the word Actions">
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            Icon-only Actions columns that offer more than view/delete carry an{' '}
            <code>ActionsHeaderHelp</code> (components/ActionsHeaderHelp.tsx) in the header —
            it opens a legend defining each icon from the one glossary
            (components/actionIconGlossary.ts). Declare the keys in the buttons&apos;
            left-to-right order; view/delete-only columns stay trigger-free (PRD D1). Use{' '}
            <code>tone=&quot;default&quot;</code> outside purple headers, and{' '}
            <code>ActionsGridHeader</code> + <code>gridContext.actionsHelpIcons</code> for AG Grid.
          </p>
          <StandardTable>
            <StandardTableHeader>
              <StandardTableRow>
                <StandardTableHeaderCell>Report</StandardTableHeaderCell>
                <StandardTableHeaderCell align="right" columnType="actions">
                  <span className="inline-flex items-center gap-1.5">
                    Actions
                    <ActionsHeaderHelp
                      icons={['run', 'preview', { key: 'edit', note: 'Org admins only.' }, 'delete']}
                    />
                  </span>
                </StandardTableHeaderCell>
              </StandardTableRow>
            </StandardTableHeader>
            <StandardTableBody>
              <StandardTableRow>
                <StandardTableCell>Weekly digest</StandardTableCell>
                <StandardTableCell align="right">
                  <div className="flex items-center justify-end gap-0.5 text-text-tertiary dark:text-text-dark-tertiary">
                    <Icons.RunIcon className="w-4 h-4" />
                    <Icons.PreviewIcon className="w-4 h-4" />
                    <Icons.EditIcon className="w-4 h-4" />
                    <Icons.DeleteIcon className="w-4 h-4" />
                  </div>
                </StandardTableCell>
              </StandardTableRow>
            </StandardTableBody>
          </StandardTable>
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary flex items-center gap-1.5">
            The default tone, for grey headers:
            <ActionsHeaderHelp tone="default" icons={['edit', 'delete']} />
          </p>
        </Section>

        <Section icon={Table2} title="Grouped table — Group By + expandable groups">
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            For ledger-style records with meaningful groupings (components/GroupedTable.tsx).
            A list that is only ever flat belongs in StandardTable. Reference adopter: workspace Notes tab.
          </p>
          <GroupedTableContainer>
            <GroupedTableToolbar
              value={demoGroupBy}
              onChange={setDemoGroupBy}
              options={[
                { id: 'none', label: 'None (ledger)' },
                { id: 'client', label: 'Client' },
                { id: 'bill', label: 'Bill' },
              ]}
              summary="6 records · 2 clients"
            />
            <GroupedTableHeader grid={DEMO_GROUP_GRID}>
              <GroupedTableHeaderCell
                label="Latest"
                sortKey="latest"
                defaultDir="desc"
                sort={{ sortBy: 'latest', sortDir: 'desc', onSort: () => undefined }}
              />
              <GroupedTableHeaderCell
                label="Client"
                sortKey="name"
                sort={{ sortBy: 'latest', sortDir: 'desc', onSort: () => undefined }}
              />
              <GroupedTableHeaderCell
                label="Records"
                sortKey="count"
                defaultDir="desc"
                alignRight
                sort={{ sortBy: 'latest', sortDir: 'desc', onSort: () => undefined }}
              />
            </GroupedTableHeader>
            <GroupedTableGroupRow
              grid={DEMO_GROUP_GRID}
              open={demoOpenGroup === 'acme'}
              onToggle={() => setDemoOpenGroup(demoOpenGroup === 'acme' ? null : 'acme')}
              name="Acme Advocacy Group"
              sub="Corporation"
              before={<span className="tabular-nums text-xs text-text-secondary dark:text-text-dark-secondary">{formatDate('2026-06-16')}</span>}
              after={<GroupedTableMetricCell primary value="4" />}
            />
            {demoOpenGroup === 'acme' && (
              <>
                {[
                  { d: '2026-06-16', t: 'HB26-1002 · Supporting' },
                  { d: '2026-05-21', t: 'SB26-0141 · Monitoring' },
                ].map((r) => (
                  <GroupedTableRow key={r.t} grid={DEMO_GROUP_GRID}>
                    <span className="tabular-nums text-xs text-text-secondary dark:text-text-dark-secondary">{formatDate(r.d)}</span>
                    <div className="min-w-0 flex items-start gap-1.5 pl-5">
                      <span className="mt-0.5"><ChildRowMarker /></span>
                      <span className="truncate text-[12.5px] text-text-primary dark:text-text-dark-primary">{r.t}</span>
                    </div>
                    <span />
                  </GroupedTableRow>
                ))}
              </>
            )}
            <GroupedTableGroupRow
              grid={DEMO_GROUP_GRID}
              open={demoOpenGroup === 'brighter'}
              onToggle={() => setDemoOpenGroup(demoOpenGroup === 'brighter' ? null : 'brighter')}
              name="Brighter Future Coalition"
              sub="Corporation"
              before={<span className="tabular-nums text-xs text-text-secondary dark:text-text-dark-secondary">{formatDate('2026-04-30')}</span>}
              after={<GroupedTableMetricCell primary value="2" />}
            />
            {demoOpenGroup === 'brighter' && <GroupedTableNotice>No individual records found.</GroupedTableNotice>}
          </GroupedTableContainer>
        </Section>

        <Section icon={Component} title="Empty states — the hint says why">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="border border-border-light dark:border-border-dark rounded-lg">
              <EmptyState icon={Icons.PeopleIcon} title="No members yet" hint="Invite someone to collaborate." size="compact" />
            </div>
            <div className="border border-border-light dark:border-border-dark rounded-lg">
              <EmptyState icon={Icons.SearchIcon} title="No results" hint="Try adjusting your filters." size="compact" />
            </div>
            <div className="border border-border-light dark:border-border-dark rounded-lg">
              <EmptyState tone="error" title="Failed to load" hint="Try again in a moment." size="compact" />
            </div>
          </div>
        </Section>

        <Section icon={Component} title="Committee names — CommitteeLink + CommitteeCard">
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            Every campaign committee name in either app renders through <code>components/committee/CommitteeLink</code>, which
            opens the committee card (fixtures here; in product it loads <code>/campaign-finance/committees/:co_id/brief</code>).
            Never a <code>committee_name</code> explorer link or a name search — guarded by <code>committeeLinkConventions.test.ts</code>.
          </p>
          <ul className="text-sm space-y-1">
            <li>Issue committee: <CommitteeLink coId={DEMO_ISSUE_COMMITTEE.co_id} name={DEMO_ISSUE_COMMITTEE.name} brief={DEMO_ISSUE_COMMITTEE} className="text-primary-700 dark:text-primary-dark-400" /></li>
            <li>Candidate committee: <CommitteeLink coId={DEMO_CANDIDATE_COMMITTEE.co_id} name={DEMO_CANDIDATE_COMMITTEE.name} brief={DEMO_CANDIDATE_COMMITTEE} className="text-primary-700 dark:text-primary-dark-400" /></li>
            <li>Unmatched name: <CommitteeLink name="Committee With No Filing" brief={null} className="text-primary-700 dark:text-primary-dark-400" /></li>
          </ul>
        </Section>

        <Section icon={Component} title="Report a problem — ReportProblemLink + ReportProblemDialog">
          <ReportProblemDemo />
        </Section>

        <Section icon={Type} title="Icons — one glyph per meaning (components/icons.ts)">
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
            {SEMANTIC_ICONS.map(([name, Icon]) => (
              <div key={name} className="flex items-center gap-2 text-sm text-text-secondary dark:text-text-dark-secondary">
                <Icon className="w-4 h-4 text-text-primary dark:text-text-dark-primary" aria-hidden="true" />
                {name}
              </div>
            ))}
          </div>
        </Section>

        <Section icon={Type} title="Data display">
          <ul className="text-sm text-text-secondary dark:text-text-dark-secondary space-y-1">
            <li>Date: <span className="text-text-primary dark:text-text-dark-primary">{formatDate('2026-01-15')}</span> — utils/dateUtils.formatDate</li>
            <li>Timestamp (browser-local by design): <span className="text-text-primary dark:text-text-dark-primary">{formatDateTime('2026-01-15T20:00:00Z')}</span></li>
            <li>Dense tables only — two-line pair: <span className="text-text-primary dark:text-text-dark-primary">{formatDateShort('2026-01-15')} / {formatTime('2026-01-15T20:00:00Z')}</span> — formatDateShort + formatTime</li>
            <li>Blank value: <span className="text-text-primary dark:text-text-dark-primary">{EMPTY_VALUE}</span> — never “N/A” or “-”</li>
          </ul>
        </Section>
      </div>
    </PageShell>
  );
};

export default DesignSystemPage;
